/**
 * What the `/s/:slug` landing does with a resolved share-link config (share links 3.1, S11).
 *
 * The landing lives on the minting origin, which serves no AR runtime. A phone that reached it was
 * not taken by the app, so the OS has already had its turn (R2, R3) and the page obeys the browser
 * routes. An iPhone never runs the web viewer: it is offered the App Clip, Safari when it is in
 * another browser, and the App Store. An Android phone runs the story in the viewer where the
 * author allowed it, except inside an app's built-in WebView, which cannot run WebXR, where it is
 * offered Chrome. A desktop never runs the viewer: it shows the link's QR code and the stores.
 */
import type { components } from '@/generated/contracts/api-v1';
import type { V1Result } from '@/infrastructure/api/v1/client';

export type ShareLinkConfig = components['schemas']['ShareLinkConfigDto'];
export type ShareRoute = components['schemas']['ShareRouteDto'];

export type ShareResolve =
  | { outcome: 'config'; config: ShareLinkConfig }
  | { outcome: 'dead' }
  | { outcome: 'unknown' }
  | { outcome: 'error'; message: string };

/** What resolveShareLink answered: 410 share_link_dead and 404 not_found are outcomes, not failures. */
export function readShareResolve(result: V1Result<ShareLinkConfig>): ShareResolve {
  if (!result.error) return { outcome: 'config', config: result.data };
  if (result.error.status === 410) return { outcome: 'dead' };
  if (result.error.status === 404) return { outcome: 'unknown' };
  return { outcome: 'error', message: result.error.message };
}

export type LandingOs = 'ios' | 'android' | 'desktop';

export interface LandingBrowser {
  os: LandingOs;
  /** iOS: Safari itself, or an app's Safari view, the only places the App Clip card appears. */
  safari: boolean;
  /** Android: an app's built-in WebView, which cannot run WebXR. */
  webView: boolean;
}

// Browsers and in-app browsers that carry Safari's token in their user agent but are not Safari
const IOS_NOT_SAFARI = /crios|fxios|edgios|opios|gsa\/|instagram|fban|fbav|linkedinapp|line\/|twitter|snapchat|musical_ly|bytedance/i;
// Apps that open links in their own WebView; `; wv)` is Android WebView's own marker
const ANDROID_WEBVIEW = /; wv\)|instagram|fban|fbav|linkedinapp|line\/|snapchat|musical_ly|bytedance/i;

export function detectBrowser(userAgent: string, maxTouchPoints = 0): LandingBrowser {
  const ua = userAgent.toLowerCase();
  const isIPad = /macintosh/.test(ua) && maxTouchPoints > 1;
  if (/iphone|ipad|ipod/.test(ua) || isIPad) {
    return { os: 'ios', safari: /safari\//.test(ua) && !IOS_NOT_SAFARI.test(ua), webView: false };
  }
  if (/android/.test(ua)) return { os: 'android', safari: false, webView: ANDROID_WEBVIEW.test(userAgent) };
  return { os: 'desktop', safari: false, webView: false };
}

export interface LandingInputs {
  config: ShareLinkConfig;
  browser: LandingBrowser;
  /** `https://xr.lureo.com`, or undefined when unconfigured (3.7). */
  webxrBase: string | undefined;
  /** Query carried from the landing URL, e.g. a pre-made `open-mode`. */
  search: string;
  /** This link as people share it, for the QR code and Copy link: the page's own URL, query included, so a
   *  pre-made `open-mode` reaches the phone that scans it. */
  shareUrl: string;
  iosStoreUrl: string;
  androidStoreUrl: string;
}

export type LandingDecision =
  | { kind: 'hop'; url: string }
  | { kind: 'appClip'; title: string; storeUrl: string | null; inSafari: boolean }
  | { kind: 'qr'; title: string; link: string }
  | { kind: 'openInChrome'; title: string; chromeUrl: string; storeUrl: string | null }
  | { kind: 'pin'; title: string; pin: string | null }
  | { kind: 'message'; title: string; body: string };

const BUCKET: Record<LandingOs, keyof ShareLinkConfig['routes']> = { ios: 'iosInAppBrowser', android: 'androidNoApp', desktop: 'desktop' };

function viewerUrl(webxrBase: string | undefined, slug: string, search: string): string | null {
  if (!webxrBase) return null;
  return `${webxrBase.replace(/\/$/, '')}/s/${encodeURIComponent(slug)}${search}`;
}

// The install referrer carries the link, as /open does, so the app can open the story after install
export function withPlayReferrer(storeUrl: string, slug: string, search: string): string {
  try {
    const url = new URL(storeUrl);
    if (url.hostname !== 'play.google.com' || url.searchParams.has('referrer')) return storeUrl;
    const referrer = new URLSearchParams([['share-link', slug], ...new URLSearchParams(search)]);
    url.searchParams.set('referrer', referrer.toString());
    return url.toString();
  } catch {
    return storeUrl;
  }
}

export const SPECTACLES_ONLY_BODY = 'This story plays on Snap Spectacles, through the Lureo lens. Ask whoever shared it for its Spectacles code.';

/** Chrome on Android, from a WebView that cannot run the viewer; the link itself is the fallback. */
export function chromeIntentUrl(url: string): string {
  const { protocol, host, pathname, search } = new URL(url);
  return `intent://${host}${pathname}${search}#Intent;scheme=${protocol.slice(0, -1)};package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end`;
}

export function decideLanding(input: LandingInputs): LandingDecision {
  const { config, browser, search } = input;
  const title = config.subject.title || 'A Lureo story';
  const route = config.routes[BUCKET[browser.os]];
  const androidStoreUrl = withPlayReferrer(input.androidStoreUrl, config.slug, search);

  if (!config.subject.sceneUniqueId || route?.do === 'notAvailable') {
    const reason = route?.reason;
    if (reason === 'no_live_version' || reason === 'project_unpublished' || !config.subject.sceneUniqueId) {
      return { kind: 'message', title, body: 'This story is not live right now. Try again once its creator has published it.' };
    }
  }

  // No phone or browser plays it, so a QR code or a store would lead nowhere
  const { publishedTo } = config;
  if (publishedTo.spectacles && !publishedTo.app && !publishedTo.web && (route?.do === 'notAvailable' || route?.do === 'showQr')) {
    return { kind: 'message', title, body: SPECTACLES_ONLY_BODY };
  }

  if (route?.do === 'notAvailable') return { kind: 'message', title, body: 'This story is not available on this device.' };

  switch (route?.do) {
    case 'showQr':
      return { kind: 'qr', title, link: input.shareUrl };
    case 'showPin':
      return { kind: 'pin', title, pin: route.pin ?? null };
    case 'openAppClip':
      return { kind: 'appClip', title, storeUrl: route.url || null, inSafari: browser.safari };
    case 'goToStore':
      // A store route redirects rather than renders: the recipient lands on the store itself
      if (browser.os === 'desktop') return { kind: 'qr', title, link: input.shareUrl };
      // An unset store URL arrives as "", which would hop to this page again
      if (browser.os === 'ios') return { kind: 'hop', url: route.url || input.iosStoreUrl };
      return { kind: 'hop', url: withPlayReferrer(route.url || input.androidStoreUrl, config.slug, search) };
    case 'openWeb':
    case 'openScene':
    case 'askWhere': {
      const browserUrl = viewerUrl(input.webxrBase, config.slug, search);
      if (!browserUrl) return { kind: 'message', title, body: 'This story cannot be opened here right now. Try again later.' };
      if (browser.os === 'android' && browser.webView) {
        return {
          kind: 'openInChrome',
          title,
          chromeUrl: chromeIntentUrl(browserUrl),
          storeUrl: config.publishedTo.app ? androidStoreUrl : null,
        };
      }
      return { kind: 'hop', url: browserUrl };
    }
    default:
      return { kind: 'message', title, body: 'This story cannot be opened here. Try the Lureo app.' };
  }
}
